import { tutorialAssets } from "assets/cdn";
import { useImagePath } from "hooks/useImagePath";
import { useMediaQuery } from "@mui/material";
import Button from "components/Buttons/ThemeButton";
import { TopBarButton } from "components/PageTopNavV2/styled";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { useWebStoreContext } from "contexts";
import { useNavigationMenu } from "hooks/useNavigationMenu";
import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";
import { ShopIcon } from "utils/svgIcons";
import { Arrow, Background2, Shine, Text, Tiki2 } from "../../styles";
import { ArrowContainer, Container, SlideNumberContainer } from "./style";

interface Slide1Props {
	onNext: () => void;
	onSkip: () => void;
	slideNumber: number;
}

export const Slide1 = ({ onNext, onSkip, slideNumber }: Slide1Props) => {
	const { getImage } = useImagePath("cdn");
	const background = getImage(tutorialAssets.background);
	const arrowUp = getImage(tutorialAssets.greenArrow);
	const shineAsset = getImage(tutorialAssets.shine);
	const tiki = getImage(tutorialAssets.char2);
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const scale = useCalculateScale(
		isPortrait ? 400 : 500,
		isPortrait ? 200 : 400
	);
	const [mainMenuPosition, setMainMenuPosition] = useState<DOMRect | null>(
		null
	);
	const buttonRef = useRef<HTMLDivElement | null>(null);

	const { userTracker, nowTime } = useWebStoreContext();
	const navigation = useNavigationMenu(
		userTracker?.clubId,
		false,
		userTracker,
		nowTime
	)?.filter(
		(navItem) =>
			!navItem?.isExternal &&
			navItem?.isEnabled &&
			navItem?.isDisplayInPageTopNav
	);

	const handleIOSScroll = async () => {
		return new Promise<void>((resolve) => {
			// timeout and top -1 needed in order to trigger scroll on iOS when the scroll is at 0
			setTimeout(() => {
				window.scrollTo(-1, 0);
				window.scrollTo({ top: -1, left: 0, behavior: "instant" });
				document.documentElement.scrollTop = -1;
				document.body.scrollTop = -1;
				resolve();
			}, 10);
		});
	};

	const handleResize = () => {
		const mainMenu = document.getElementById("page-top-nav-main-menu-store");
		setMainMenuPosition(mainMenu?.getBoundingClientRect() as DOMRect);
	};

	const debounceResize = debounce(handleResize, 300);

	useEffect(() => {
		if (navigation?.length) {
			if (window.scrollY > 0) {
				handleIOSScroll().then(() => {
					debounceResize();
				});
			} else {
				debounceResize();
			}
		}
	}, [navigation?.length, buttonRef?.current]);

	useEffect(() => {
		window.addEventListener("resize", debounceResize);
		window.addEventListener("scrollend", debounceResize);
		window.addEventListener("transitionend", debounceResize);

		return () => {
			window.removeEventListener("resize", debounceResize);
			window.removeEventListener("scrollend", debounceResize);
			window.removeEventListener("transitionend", debounceResize);
		};
	}, []);

	return (
		<>
			{window.scrollY === 0 && (
				<>
					<TopBarButton
						ref={buttonRef}
						className="default-nav is-active"
						style={{
							position: "fixed",
							top: mainMenuPosition?.top,
							left: mainMenuPosition?.left,
							height: "29px",
							width: "85px",
							transition: "none"
						}}
					>
						<ShopIcon />
						<span>
							{navigation?.find((navItem) => navItem.icon === "store")?.title}
						</span>
					</TopBarButton>
					<Shine
						src={shineAsset}
						width={200}
						height={200}
						className="animate"
						position="fixed"
						top={`${
							(mainMenuPosition?.top || 0) + (mainMenuPosition?.height || 0) / 2
						}px`}
						left={`${
							(mainMenuPosition?.left || 0) + (mainMenuPosition?.width || 0) / 2
						}px`}
					/>
					<Container
						scale={scale}
						top={
							((mainMenuPosition?.top || 0) +
								(mainMenuPosition?.height || 0) +
								10) *
							scale
						}
						left={
							(mainMenuPosition?.left || 0) +
							(mainMenuPosition?.width || 0) / 2 -
							45 * scale
						}
					>
						<ArrowContainer>
							<Arrow src={arrowUp} alt="Arrow up" width={46} />
						</ArrowContainer>
						<SlideNumberContainer>
							<Text
								color="#fff"
								fontFamily="Baloo"
								fontSize="16px"
								lineHeight="17px"
							>
								1/{slideNumber}
							</Text>
						</SlideNumberContainer>
						<div>
							<Background2 $backgroundImage={background}>
								<div>
									<Tiki2 src={tiki} alt="Tiki" bottom="10px" />
								</div>
								<div>
									<Text
										color="#462C12"
										fontFamily="Inter"
										fontSize="16px"
										fontWeight={700}
										lineHeight="17px"
									>
										Earn Loyalty Points with
										<br /> every web purchase!
									</Text>
									<Text
										color="#462C12"
										fontFamily="Inter"
										fontSize="12px"
										lineHeight="15px"
									>
										Buy on the web and get LP
										<br /> automatically.
									</Text>
								</div>
								<Button className="next" onClick={onNext}>
									Next
								</Button>
								<Button className="skip" variant="text" onClick={onSkip}>
									Skip
								</Button>
							</Background2>
						</div>
					</Container>
				</>
			)}
		</>
	);
};
